import { useEffect, useState } from 'react'
import { Bell, BellOff, BellRing, X } from 'lucide-react'
import { pushAPI } from '../api'
// Стили подсказки общие с кнопкой установки — она стоит рядом и это та же по смыслу
// вещь: настройка приложения на конкретном устройстве, а не действие над данными.
import './InstallAppButton.css'

/**
 * Включалка push-уведомлений о лимитах чужих подписок.
 *
 * Сервер шлёт уведомление, когда у привязанного источника кончается трафик или срок.
 * Разрешение спрашивается ТОЛЬКО по клику: Notification.requestPermission(), вызванный
 * из useEffect, Chrome отклоняет автоматически, без всякого диалога.
 */
export default function PushToggle() {
  const [supported, setSupported] = useState(false)
  const [permission, setPermission] = useState('default')
  const [subscribed, setSubscribed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [hint, setHint] = useState(null)
  const [status, setStatus] = useState(null)

  useEffect(() => {
    const ok = 'Notification' in window && 'PushManager' in window && 'serviceWorker' in navigator
    setSupported(ok)
    if (!ok) return
    setPermission(Notification.permission)
    ;(async () => {
      try {
        const reg = await navigator.serviceWorker.ready
        const sub = await reg.pushManager.getSubscription()
        setSubscribed(!!sub)
        // Переподписываемся при каждом открытии дашборда. На сервере это upsert по хешу
        // эндпоинта, то есть no-op — но так чинится случай, когда Chrome провернул
        // эндпоинт, пока дашборд был закрыт. Своего обработчика pushsubscriptionchange
        // в SW сделать нельзя: JWT лежит в localStorage, воркеру он недоступен.
        if (sub && Notification.permission === 'granted') {
          await pushAPI.subscribe(sub.toJSON()).catch(() => {})
        }
      } catch { /* SW ещё не готов — не беда, кнопка просто покажет «выключено» */ }
    })()
  }, [])

  if (!supported) return null

  const enable = async () => {
    setBusy(true)
    try {
      const { data } = await pushAPI.vapidKey()

      const perm = await Notification.requestPermission()
      setPermission(perm)
      if (perm !== 'granted') { setHint('denied'); return }

      // .ready, а не .register — регистрацию уже сделал registerSW.js
      const reg = await navigator.serviceWorker.ready
      let sub = await reg.pushManager.getSubscription()

      // Если подписка выдана под ДРУГОЙ ключ VAPID (сервер пересоздал пару), она мертва
      // навсегда — push-сервис будет отвечать 403. Молча переподписываемся.
      if (sub && !sameKey(sub.options?.applicationServerKey, data.key)) {
        await sub.unsubscribe()
        sub = null
      }

      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true, // в Chrome обязателен, иначе subscribe() отклонится
          applicationServerKey: urlBase64ToUint8Array(data.key),
        })
      }

      // toJSON() отдаёт ровно {endpoint, keys:{p256dh, auth}} — форму, которую ждёт сервер
      await pushAPI.subscribe(sub.toJSON())
      setSubscribed(true)
      await refreshStatus()
    } catch (err) {
      console.error('[push]', err)
      setHint('error:' + (err?.response?.data?.error || err.message))
    } finally {
      setBusy(false)
    }
  }

  const disable = async () => {
    setBusy(true)
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        await pushAPI.unsubscribe(sub.endpoint).catch(() => {})
        await sub.unsubscribe()
      }
      setSubscribed(false)
      setHint(null)
    } finally {
      setBusy(false)
    }
  }

  const refreshStatus = async () => {
    try { setStatus((await pushAPI.status()).data) } catch { setStatus(null) }
  }

  const openPanel = async () => {
    if (permission === 'denied') { setHint(hint === 'denied' ? null : 'denied'); return }
    if (!subscribed) return enable()
    if (hint === 'panel') { setHint(null); return }
    setHint('panel')
    refreshStatus()
  }

  const Icon = permission === 'denied' ? BellOff : subscribed ? BellRing : Bell

  return (
    <div className="install-app-wrap">
      <button
        onClick={openPanel}
        disabled={busy}
        className="btn btn-secondary btn-sm"
        title={
          permission === 'denied' ? 'Уведомления заблокированы в браузере'
            : subscribed ? 'Уведомления включены' : 'Включить уведомления о лимитах подписок'
        }
      >
        <Icon size={14} />
        <span className="btn-label"> {subscribed ? 'Уведомления' : 'Уведомления'}</span>
      </button>

      {hint && (
        <div className="install-hint" role="dialog">
          <button className="install-hint-close" onClick={() => setHint(null)} title="Закрыть">
            <X size={13} />
          </button>

          {hint === 'denied' && (<>
            <div className="install-hint-title">Уведомления заблокированы</div>
            <div className="install-hint-row">
              Вернуть можно только руками: замочек в адресной строке → «Уведомления» → «Разрешить», потом обновить страницу.
            </div>
          </>)}

          {String(hint).startsWith('error:') && (<>
            <div className="install-hint-title">Не получилось</div>
            <div className="install-hint-row">{String(hint).slice(6)}</div>
          </>)}

          {hint === 'panel' && (<>
            <div className="install-hint-title">Уведомления включены</div>
            {/* Доставку push мы не контролируем: молчание «всё хорошо» неотличимо от
                молчания «сломалось». Эта строка их и различает. */}
            <div className="install-hint-row">
              {status?.watcher?.lastResult
                ? <>Последняя проверка: {fmtAgo(status.watcher.lastRun)} · источников {status.watcher.lastResult.sources} · предупреждений {status.watcher.lastResult.warnings}</>
                : <>Проверка ещё не проходила — первая будет в течение часа.</>}
            </div>
            <div className="install-hint-row">
              Пороги: {status?.watcher?.pctSteps?.join('% / ')}% трафика, {status?.watcher?.daySteps?.join(' и ')} дн. до конца
            </div>
            {status?.warnings?.length > 0 && (
              <div className="install-hint-row"><b>Сейчас за порогом:</b> {status.warnings.length} источник(ов)</div>
            )}
            <div className="install-hint-row" style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button className="btn btn-secondary btn-xs" disabled={busy}
                      onClick={() => pushAPI.test().catch(() => {})}>Тест</button>
              <button className="btn btn-secondary btn-xs" disabled={busy}
                      onClick={() => pushAPI.checkNow().then(() => setTimeout(refreshStatus, 3000)).catch(() => {})}>
                Проверить сейчас
              </button>
              <button className="btn btn-secondary btn-xs" disabled={busy} onClick={disable}>Выключить</button>
            </div>
          </>)}
        </div>
      )}
    </div>
  )
}

function fmtAgo(iso) {
  if (!iso) return 'никогда'
  const min = Math.round((Date.now() - Date.parse(iso)) / 60000)
  if (min < 1) return 'только что'
  if (min < 60) return `${min} мин назад`
  return `${Math.round(min / 60)} ч назад`
}

/** base64url от сервера → Uint8Array для applicationServerKey (обязано выйти 65 байт с 0x04). */
function urlBase64ToUint8Array(base64) {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4)
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/** Совпадает ли ключ действующей подписки с тем, что сейчас у сервера. */
function sameKey(abuf, b64) {
  // Firefox исторически отдавал тут null. Считаем это «совпадает»: иначе получится
  // бесконечный цикл отписки-подписки на каждом открытии дашборда.
  if (!abuf) return true
  const a = new Uint8Array(abuf)
  const b = urlBase64ToUint8Array(b64)
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}
